import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
// @mui
import { Box, Stack, Drawer, Link, Typography, ListItemButton, ListItemIcon, ListItemText, alpha, IconButton, useTheme } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// config
import { NAV } from '../../../config';
// components
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import Iconify from '../../../components/iconify';

// ----------------------------------------------------------------------

const navItems = [
  {
    title: 'Search Companies',
    path: '/dashboard',
    icon: 'eva:search-fill',
  },
  {
    title: 'API',
    path: 'https://api.rivadata.com',
    icon: 'eva:code-fill',
    external: true,
  },
];

// ----------------------------------------------------------------------

NavVerticalAuth.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
  width: PropTypes.number,
  isCollapsed: PropTypes.bool,
  isResizing: PropTypes.bool,
  onResizeStart: PropTypes.func,
};

export default function NavVerticalAuth({ openNav, onCloseNav, width, isCollapsed, isResizing, onResizeStart }) {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDesktop = useResponsive('up', 'lg');
  const displayWidth = width || NAV.W_DASHBOARD;

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);


  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Logo */}
      <Box sx={{ px: isCollapsed ? 2 : 3, py: 3, display: 'flex', alignItems: 'center', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
        {isCollapsed ? (
          <Box
            component="img"
            src="/logo/riva-logo-square.png"
            sx={{ width: 32, height: 32 }}
            alt="Riva Logo"
          />
        ) : (
          <Logo />
        )}
      </Box>

      {/* Navigation Items */}
      <Box sx={{ px: 2, py: 2 }}>
        <Stack spacing={0.5}>
          {navItems.map((item) => {
            const isActive = pathname === item.path;
            
            return (
              <ListItemButton
                key={item.title}
                component={item.external ? Link : RouterLink}
                href={item.external ? item.path : undefined}
                to={!item.external ? item.path : undefined}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noopener noreferrer' : undefined}
                sx={{
                  py: 0.75,
                  px: isCollapsed ? 1.5 : 2,
                  mb: 0.5,
                  borderRadius: 0.75,
                  typography: 'body2',
                  color: 'text.secondary',
                  fontWeight: 'fontWeightRegular',
                  transition: 'all 0.2s',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                    color: 'text.primary',
                  },
                  ...(isActive && {
                    color: 'text.primary',
                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                    fontWeight: 'fontWeightMedium',
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
                    },
                  }),
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isCollapsed ? 0 : 32,
                    color: 'inherit',
                    opacity: 0.8,
                  }}
                >
                  <Iconify icon={item.icon} width={18} height={18} />
                </ListItemIcon>
                {!isCollapsed && (
                  <>
                    <ListItemText 
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: 'inherit',
                        noWrap: true,
                        sx: {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                    {item.external && (
                      <Iconify 
                        icon="eva:external-link-fill" 
                        width={16} 
                        height={16}
                        sx={{ ml: 1, color: 'text.disabled' }}
                      />
                    )}
                  </>
                )}
              </ListItemButton>
            );
          })}
        </Stack>
      </Box>

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: isCollapsed ? NAV.W_DASHBOARD_MINI : displayWidth },
        height: { xs: 'auto', lg: '100vh' },
        position: 'relative',
        transition: isResizing ? 'none' : 'width 0.2s',
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: isCollapsed ? NAV.W_DASHBOARD_MINI : displayWidth,
              bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
              borderRight: `1px solid ${theme.palette.divider}`,
              transition: isResizing ? 'none' : 'width 0.2s',
              position: 'fixed',
              top: 0,
              bottom: 0,
              overflow: 'hidden',
              height: '100vh',
              boxShadow: 'none',
              borderRadius: 0,
              borderBottom: 'none', // Remove bottom border
            },
          }}
        >
          {renderContent}
          {/* Resize Handle */}
          {!isCollapsed && (
            <Box
              onMouseDown={onResizeStart}
              sx={{
                position: 'absolute',
                right: -2,
                top: 0,
                bottom: 0,
                width: 5,
                cursor: 'col-resize',
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2),
                },
                ...(isResizing && {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.3),
                }),
              }}
            />
          )}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          anchor="left"
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV.W_DASHBOARD,
              bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
              boxShadow: `40px 40px 80px -8px ${alpha(
                theme.palette.mode === 'light' ? theme.palette.grey[500] : theme.palette.common.black,
                0.24
              )}`,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}